const AvivaUi = (() => {
  'use strict';

  const onMedia = (query, callback) => {
    if (!query) {
      callback({ matches: true, media: 'all' });
      return () => {};
    }

    const mediaQuery = 'string' === typeof query ? window.matchMedia(query) : query;
    const handleChange = () => callback(mediaQuery);

    if ('function' === typeof mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    handleChange();

    return () => {
      if ('function' === typeof mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else if ('function' === typeof mediaQuery.removeListener) {
        mediaQuery.removeListener(handleChange);
      }
    };
  };

  const createFrameScheduler = (callback) => {
    let frame = null;
    let destroyed = false;

    const schedule = () => {
      if (destroyed) {
        return;
      }

      if (null !== frame) {
        window.cancelAnimationFrame(frame);
      }

      frame = window.requestAnimationFrame(() => {
        frame = null;
        callback();
      });
    };

    schedule.cancel = () => {
      if (null !== frame) {
        window.cancelAnimationFrame(frame);
        frame = null;
      }
    };

    schedule.destroy = () => {
      destroyed = true;
      schedule.cancel();
    };

    return schedule;
  };

  const getCssDuration = (element, propertyName) => {
    const duration = window.getComputedStyle(element).getPropertyValue(propertyName).trim();
    const durationValue = parseFloat(duration) || 0;

    return duration.endsWith('ms') ? durationValue : durationValue * 1000;
  };

  // Durations, thresholds and similar config: only a positive number replaces the fallback.
  const getDataNumber = (element, key, fallback) => {
    const value = element && element.dataset && element.dataset[key] ? element.dataset[key].trim() : '';
    const number = Number(value);

    return '' !== value && Number.isFinite(number) && number > 0 ? number : fallback;
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Shared play/pause control for moving content (footer video, module videos, specs rotation).
  // Motion starts on its own only when the visitor hasn't asked for reduced motion. The
  // button shows only to reduced-motion visitors, who start paused and can press Play
  // (a design decision of 21 Oct 2026; see the deployment file). A visitor's own play or
  // pause holds through resizes and scrolling until they press it again or change the setting.
  const createMotionControl = ({
    toggle = null,
    visibleLabel = null,
    play: playMotion,
    pause: pauseMotion,
    isPaused,
    shouldAutoplay = () => true,
  }) => {
    let userChoice = null; // null, 'play' or 'pause'.

    const sync = () => {
      if (!toggle) {
        return;
      }

      const paused = isPaused();
      const label = paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;

      toggle.hidden = !reducedMotion.matches;
      toggle.classList.toggle('is-paused', paused);
      toggle.setAttribute('aria-label', label);

      if (visibleLabel) {
        visibleLabel.textContent = label;
      }
    };

    // video.play() returns a promise that rejects when playback is blocked.
    const run = (callback) => {
      let result;

      try {
        result = callback();
      } catch (error) {
        // Report it without stopping the other controls being set up.
        window.setTimeout(() => {
          throw error;
        });
        return;
      }

      if (result && 'function' === typeof result.catch) {
        result.catch(sync);
      }
    };

    const wantsMotion = () => (
      null === userChoice ? !reducedMotion.matches : 'play' === userChoice
    );

    // Bring the motion in line with the visitor's choice, their setting and the module's
    // own conditions (for example, being on screen).
    const update = () => {
      run(wantsMotion() && shouldAutoplay() ? playMotion : pauseMotion);
      sync();
    };

    if (toggle) {
      toggle.addEventListener('click', () => {
        userChoice = isPaused() ? 'play' : 'pause';
        run('play' === userChoice ? playMotion : pauseMotion);
        sync();
      });
    }

    // A change to the motion setting is a fresh instruction, so it replaces an earlier choice.
    onMedia(reducedMotion, () => {
      userChoice = null;
      update();
    });

    return {
      sync,
      update,
    };
  };

  // Bootstrap's own breakpoints, so the SCSS stays the only place they're set.
  const breakpointFallbacks = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1440,
  };

  // Matches media-breakpoint-down(name): below the named breakpoint.
  const getBreakpointDownMedia = (name) => {
    const value = window.getComputedStyle(document.documentElement)
      .getPropertyValue(`--bs-breakpoint-${name}`);
    const breakpoint = parseFloat(value) || breakpointFallbacks[name];

    return window.matchMedia(`(max-width: ${breakpoint - 0.02}px)`);
  };

  const watchLayoutSources = ({
    images = [],
    persistentImages = [],
    videos = [],
    fonts = false,
    resize = false,
  }, callback) => {
    let active = true;
    const cleanups = [];
    const addListener = (target, eventName, options) => {
      target.addEventListener(eventName, callback, options);
      cleanups.push(() => target.removeEventListener(eventName, callback, options));
    };

    Array.from(images).forEach((image) => {
      if (!image.complete) {
        addListener(image, 'load', { once: true });
      }
    });

    Array.from(persistentImages).forEach((image) => {
      addListener(image, 'load');
    });

    Array.from(videos).forEach((video) => {
      if (video.readyState < 1) {
        addListener(video, 'loadedmetadata', { once: true });
      }
    });

    if (fonts && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (active) {
          callback();
        }
      });
    }

    if (resize) {
      addListener(window, 'resize', { passive: true });
    }

    return () => {
      active = false;
      cleanups.forEach((cleanup) => cleanup());
    };
  };

  const initResponsiveFlickity = ({
    el,
    query = null,
    options = {},
    getOptions = null,
    enabled = (mediaQuery) => mediaQuery.matches,
    onEnable = null,
    onSelect = null,
    onLayout = null,
    onResetLayout = null,
    onDisable = null,
  }) => {
    let flickity = null;
    let enabledState = null;

    const relayout = createFrameScheduler(() => {
      if (onLayout) {
        onLayout({ el, flickity });
      }

      if (flickity) {
        flickity.resize();
      }
    });

    const disable = () => {
      relayout.cancel();

      if (flickity) {
        flickity.destroy();
        flickity = null;
      }

      if (onResetLayout) {
        onResetLayout({ el });
      }

      if (onDisable) {
        onDisable({ el });
      }
    };

    const removeMediaListener = onMedia(query, (mediaQuery) => {
      const shouldEnable = enabled(mediaQuery);

      if (shouldEnable === enabledState) {
        return;
      }

      enabledState = shouldEnable;

      if (!shouldEnable) {
        disable();
        // The resize that crossed the breakpoint may have had its layout frame
        // cancelled above, so lay the module out again for the new breakpoint.
        relayout();
        return;
      }

      if (onEnable) {
        onEnable({ el });
      }

      if ('function' === typeof window.Flickity) {
        const flickityOptions = getOptions ? getOptions() : options;

        flickity = new window.Flickity(el, flickityOptions);

        if (onSelect) {
          flickity.on('select', () => onSelect(flickity.selectedIndex, flickity));
        }
      }

      relayout();
    });

    return {
      getInstance: () => flickity,
      relayout,
      destroy: () => {
        removeMediaListener();
        disable();
        relayout.destroy();
      },
    };
  };

  return {
    getCssDuration,
    getDataNumber,
    getBreakpointDownMedia,
    createMotionControl,
    initResponsiveFlickity,
    onMedia,
    watchLayoutSources,
  };
})();

const AvivaModules = {};
let loadAvivaSageEvents = null;

(function ($) {
  ////////////////////////////////////////////////////////////////////////////////////
  // Set enquire for desktop
  ////////////////////////////////////////////////////////////////////////////////////
  enquire.register("(min-width: 768px)", function () {});

  ////////////////////////////////////////////////////////////////////////////////////
  // Set enquire for mobile
  ////////////////////////////////////////////////////////////////////////////////////
  enquire.register("(max-width: 767px)", function () {});

  ////////////////////////////////////////////////////////////////////////////////////
  // Common JS - All Pages
  ////////////////////////////////////////////////////////////////////////////////////
  var Sage = {
    common: {
      init: function () {
        ////////////////////////////////////////////////////////////////////////////////////
        // AOS - Animate on scroll init
        ////////////////////////////////////////////////////////////////////////////////////
        // Runs before the modules: AOS keeps every data-aos module hidden until it starts.
        AOS.init({
          delay: 100, // values from 0 to 3000, with step 50ms
          initClassName: 'aos-init', // class applied after initialization
          animatedClassName: 'aos-animate', // class applied on animation
          duration: 400, // values from 0 to 3000, with step 50ms
          easing: 'ease', // default easing for AOS animations
          once: true, // whether animation should happen only once - while scrolling down
          anchorPlacement: 'top-bottom', // defines which position of the element regarding to window should trigger the animation
        });

        ////////////////////////////////////////////////////////////////////////////////////
        // Flexible content modules
        ////////////////////////////////////////////////////////////////////////////////////
        // Each module starts in isolation, so one failure can't stop the rest of the page.
        [
          AvivaModules.initSpecsBlocks,
          AvivaModules.initMapModules,
          AvivaModules.initCardsBlocks,
          AvivaModules.initCarousels,
          AvivaModules.initContentShowcases,
          AvivaModules.initFooterVideos,
          AvivaModules.initModuleVideos,
          AvivaModules.initPriceEstimates,
        ].forEach(function (initModule) {
          try {
            initModule();
          } catch (error) {
            // Rethrow asynchronously so the error is still reported.
            window.setTimeout(function () {
              throw error;
            });
          }
        });




        ////////////////////////////////////////////////////////////////////////////////////
        // Recalculate AOS positions after modules such as Flickity have
        // finished changing the page layout.
        ////////////////////////////////////////////////////////////////////////////////////
        const refreshAosPositions = function () {
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              AOS.refresh();
            });
          });
        };

        // Refresh after the module initializers and their scheduled layout frames.
        refreshAosPositions();

        // Refresh again once media has loaded and when browser scroll position
        // is restored following refresh or back/forward navigation.
        if ('complete' === document.readyState) {
          refreshAosPositions();
        } else {
          window.addEventListener('load', refreshAosPositions, { once: true });
        }

        window.addEventListener('pageshow', refreshAosPositions);





        ////////////////////////////////////////////////////////////////////////////////////
        // Search Functionality
        ////////////////////////////////////////////////////////////////////////////////////
        $(document).on('click', '.js-init-sitewide-search', function () {

          ////////////////////////////////////////////////////////////////////////////////////
          // Show Search
          ////////////////////////////////////////////////////////////////////////////////////
          if ($('.js-section-site-search').is(':hidden')) {
            $('.js-section-site-search').slideDown('slow', function () {
              $('#header-search').val('').focus();
            });
          } else {
            $('.js-section-site-search').slideUp('slow');
          }

          ////////////////////////////////////////////////////////////////////////////////////
          // Return False
          ////////////////////////////////////////////////////////////////////////////////////
          return false;

        });

        //////////////////////////////////////////////////////////////
        // Animate Hamburger
        //////////////////////////////////////////////////////////////
        $('.navbar-toggler').on('click', function () {
          $(this).toggleClass('active');
          $('.navbar').toggleClass('active');
          $('body').toggleClass('navbar-active');
        });


        ////////////////////////////////////////////////////////////////////////
        // LOGO ON SCROLL
        ////////////////////////////////////////////////////////////////////////
        // Only the home page logo changes on scroll; update the mobile and desktop logo.
        const headerLogos = document.querySelectorAll('.js-header-logo.homepage');
        if (headerLogos.length) {
          const updateHeaderLogo = () => {
            headerLogos.forEach((headerLogo) => {
              const scrollThreshold = AvivaUi.getDataNumber(headerLogo, 'scrollThreshold', 120);

              headerLogo.classList.toggle('scrolled', window.scrollY > scrollThreshold);
            });
          };
          updateHeaderLogo();
          window.addEventListener('scroll', updateHeaderLogo, { passive: true });
        }


        ////////////////////////////////////////////////////////////////////////
        // RE INIT CHOSEN ON GF Submit
        ////////////////////////////////////////////////////////////////////////
        function initChosen() {
          $('.section-flexi-gravity-forms-block .gfield_select').each(function () {
            const $select = $(this);

            if (!$select.next('.chosen-container').length) {
              $select.chosen({
                width: '100%'
              });
            }
          });
        }

        $(document).ready(initChosen);

        $(document).on('gform_post_render gform/postRender', function () {
          initChosen();
        });


        ////////////////////////////////////////////////////////////////////////
        // Footer Header - Lines
        ////////////////////////////////////////////////////////////////////////
        function setFooterHeadingLineClass() {
          const lineClasses = ['one-liner', 'two-liner', 'three-liner', 'four-liner'];

          document.querySelectorAll('.js-footer-video-content').forEach(function (content) {
            const isXxlDown = AvivaUi.getBreakpointDownMedia('xxl').matches;

            content.classList.remove(...lineClasses);

            if (!isXxlDown) return;

            const heading = content.querySelector('.js-footer-heading');
            if (!heading) return;

            const lineHeight = parseFloat(getComputedStyle(heading).lineHeight);
            const lines = Math.round(heading.getBoundingClientRect().height / lineHeight);

            const classes = {
              1: 'one-liner',
              2: 'two-liner',
              3: 'three-liner',
              4: 'four-liner'
            };

            // Five or more lines keep the default logo position, so no class is added.
            if (classes[lines]) {
              content.classList.add(classes[lines]);
            }
          });
        }

        function updateFooterHeadingLines() {
          requestAnimationFrame(function () {
            requestAnimationFrame(setFooterHeadingLineClass);
          });
        }

        updateFooterHeadingLines();
        if (document.fonts) {
          document.fonts.ready.then(updateFooterHeadingLines);
        }

        let footerHeadingResizeTimer = null;
        window.addEventListener('resize', function () {
          window.clearTimeout(footerHeadingResizeTimer);
          footerHeadingResizeTimer = window.setTimeout(updateFooterHeadingLines, 150);
        }, { passive: true });


        //////////////////////////////////////////////////////////////
        // Calculate True VH
        //////////////////////////////////////////////////////////////
        // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
        // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
        let vh = window.innerHeight * 0.01;
        // Then we set the value in the --vh custom property to the root of the document
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        // We listen to the resize event
        window.addEventListener('resize', () => {
          // We execute the same script as before
          let vh = window.innerHeight * 0.01;
          document.documentElement.style.setProperty('--vh', `${vh}px`);
        });

        ////////////////////////////////////////////////////////////////////////////////////
        // Contact Form Validation
        ////////////////////////////////////////////////////////////////////////////////////
        if ($('#js-form-contact').length) {
          $('#js-form-contact').validate({
            rules: {
              email: {
                remote: {
                  url: '/wp-admin/admin-ajax.php',
                  type: "POST",
                  data: {
                    'action': 'valid_email_lookup',
                    'email_value': function () {
                      return $("#email").val();
                    },
                  },
                },
              },
              phone_number: {
                validPhone: true,
              },
              'checkbox_multiple': {
                required: true,
              },
              'radio_multiple': {
                required: true,
              }
            },
            highlight: function (element) {
              $(element).parents('.form-group').find('input, select, textarea').addClass('is-invalid').removeClass('is-valid');
              $(element).parents().children('.invalid-feedback').css('display', 'block');
              $(element).parents().children('.valid-feedback').css('display', 'none');
            },
            unhighlight: function (element) {
              $(element).parents('.form-group').find('input, select, textarea').addClass('is-valid').removeClass('is-invalid');
              $(element).parents().children('.invalid-feedback').css('display', 'none');
              $(element).parents().children('.valid-feedback').css('display', 'block');
            },
            errorPlacement: function () {
              $(this).parents('form').addClass('was-validated');
              return false;
            },
            submitHandler: function (form) {

              ////////////////////////////////////////////////////////////////////////////////////
              // Display ajax loader
              ////////////////////////////////////////////////////////////////////////////////////
              $('.js-contact-error, .js-contact-recaptcha-error, #js-form-contact').css('display', 'none');
              $('.js-contact-loader').css('display', 'block');

              ////////////////////////////////////////////////////////////////////////////////////
              // Validate reCaptcha
              ////////////////////////////////////////////////////////////////////////////////////
              generate_google_recaptcha_token('contact_form').then(function (response) {

                ////////////////////////////////////////////////////////////////////////////////////
                // reCaptcha Response
                ////////////////////////////////////////////////////////////////////////////////////
                var reCaptchaResponse = jQuery.parseJSON(response);

                ////////////////////////////////////////////////////////////////////////////////////
                // IF Invalid - Show Error
                ////////////////////////////////////////////////////////////////////////////////////
                if (reCaptchaResponse.valid == 'false') {

                  ////////////////////////////////////////////////////////////////////////////////////
                  // Display Error
                  ////////////////////////////////////////////////////////////////////////////////////
                  $('.js-contact-loader').css('display', 'none');
                  $('#js-form-contact, .js-contact-recaptcha-error').css('display', 'block');

                  ////////////////////////////////////////////////////////////////////////////////////
                  // ELSE Success
                  ////////////////////////////////////////////////////////////////////////////////////
                } else {

                  ////////////////////////////////////////////////////////////////////////////////////
                  // Ajax Variables
                  ////////////////////////////////////////////////////////////////////////////////////
                  var apiRequestError = false; // API Request Error Status

                  ////////////////////////////////////////////////////////////////////////////////////
                  // Mandrill Payload
                  ////////////////////////////////////////////////////////////////////////////////////
                  var mandrillPayload = mandrill_json_payload('#js-form-contact'); // Mandrill API Payload

                  console.log(mandrillPayload);
                  // TEMPLATE: Remove this return to enable Mandrill form submission
                  return false;

                  ////////////////////////////////////////////////////////////////////////////////////
                  // TEMPLATE: Campaign Monitor Subscriber Payload
                  ////////////////////////////////////////////////////////////////////////////////////
                  // var cmPayload = new Object();
                  // cmPayload.customFields = {}
                  // cmPayload.listID = 'a480aacea0c318f253bf9feff5d97c83';
                  // cmPayload.name = $('#first_name').val() + ' ' + $('#last_name').val();
                  // cmPayload.emailAddress = $('#email').val();

                  // cmPayload.customFields.PhoneNumber = $('#phone_number').val();
                  // cmPayload.customFields.Message = $('#message').val();
                  // cmPayload.customFields.RadioOptions = $('input[name=radioexample]:checked').val();
                  // cmPayload.customFields.SelectOption = $('#select_example').val();

                  // cmPayload.customFields.CheckboxField = [];
                  // $('[name="checkboxexample"]').each(function( index ) {
                  //   if($(this).is(':checked')) {
                  //     cmPayload.customFields.CheckboxField.push($(this).val());
                  //   }
                  // });

                  ////////////////////////////////////////////////////////////////////////////////////
                  // TEMPLATE: MailChimp Payload
                  ////////////////////////////////////////////////////////////////////////////////////
                  // var mailChimpPayload = new Object();
                  // mailChimpPayload.customFields = {}
                  // mailChimpPayload.listID = 'f0dddbd24e';
                  // mailChimpPayload.emailAddress = $('#email').val();

                  // ////////////////////////////////////////////////////////////////////////////////////
                  // // MailChimp custom fields
                  // ////////////////////////////////////////////////////////////////////////////////////
                  // mailChimpPayload.customFields.FNAME = $('#first_name').val();
                  // mailChimpPayload.customFields.LNAME = $('#last_name').val();
                  // mailChimpPayload.customFields.MERGE3 = $('#phone_number').val();

                  ////////////////////////////////////////////////////////////////////////////////////
                  // Load call to API
                  ////////////////////////////////////////////////////////////////////////////////////
                  $.when(

                    //////////////////////////////////////////////////////////////////////////////////
                    // Mandrill Payload Call
                    //////////////////////////////////////////////////////////////////////////////////
                    $.ajax({
                      type: "POST",
                      url: 'https://api.' + nous_api_data.api_endpoint + '.com.au/mandrill/notification',
                      dataType: 'json',
                      contentType: 'application/json',
                      data: mandrillPayload,
                      success: function (data) {

                        ////////////////////////////////////////////////////////////////////////////////////
                        // If there is an error with the Mandrill Call, display in console.
                        ////////////////////////////////////////////////////////////////////////////////////
                        if (data.status == 'error') {

                          ////////////////////////////////////////////////////////////////////////////////////
                          // Log Error
                          ////////////////////////////////////////////////////////////////////////////////////
                          console.log(data);
                          apiRequestError = true;

                        }

                      },
                    })

                    //////////////////////////////////////////////////////////////////////////////////
                    // Campaign Monitor Payload Call
                    //////////////////////////////////////////////////////////////////////////////////
                    // $.ajax({
                    //    type: "POST",
                    //    url: 'https://api.' + nous_api_data.api_endpoint + '.com.au/cm/subscriber/add',
                    //    dataType: 'json',
                    //    contentType: 'application/json',
                    //    data: JSON.stringify(cmPayload),
                    //    success:function(data) {

                    //       //////////////////////////////////////////////////////////////////////////////////
                    //       // If there is an error with the Mandrill Call, display in console.
                    //       //////////////////////////////////////////////////////////////////////////////////
                    //       if(data.status == 'error') {

                    //         ////////////////////////////////////////////////////////////////////////////////////
                    //         // Log Error
                    //         ////////////////////////////////////////////////////////////////////////////////////
                    //         console.log(data);
                    //         apiRequestError = true;

                    //       }

                    //    },
                    // })

                    //////////////////////////////////////////////////////////////////////////////////
                    // MailChimp Payload Call
                    //////////////////////////////////////////////////////////////////////////////////
                    // $.ajax({
                    //    type: "POST",
                    //    url: 'https://api.' + nous_api_data.api_endpoint + '.com.au/mailchimp/member/add',
                    //    dataType: 'json',
                    //    contentType: 'application/json',
                    //    data: JSON.stringify(mailChimpPayload),
                    //    success:function(data) {

                    //       //////////////////////////////////////////////////////////////////////////////////
                    //       // If there is an error with the Mandrill Call, display in console.
                    //       //////////////////////////////////////////////////////////////////////////////////
                    //       if(data.status == 'error') {

                    //         ////////////////////////////////////////////////////////////////////////////////////
                    //         // Log Error
                    //         ////////////////////////////////////////////////////////////////////////////////////
                    //         console.log(data);
                    //         apiRequestError = true;

                    //       }

                    //    },
                    // })

                    //////////////////////////////////////////////////////////////////////////////////
                    // Once all api calls are complete
                    //////////////////////////////////////////////////////////////////////////////////
                  ).then(function () {

                    ////////////////////////////////////////////////////////////////////////////////////
                    // Display Success / Error based on returned API
                    ////////////////////////////////////////////////////////////////////////////////////
                    if (apiRequestError == false) {

                      ////////////////////////////////////////////////////////////////////////////////////
                      // Success Block
                      ////////////////////////////////////////////////////////////////////////////////////
                      $('.js-contact-loader').css('display', 'none');
                      $('.js-contact-success').css('display', 'block');

                      ////////////////////////////////////////////////////////////////////////////////////
                      // Data Layer
                      ////////////////////////////////////////////////////////////////////////////////////
                      dataLayer.push({
                        'event': 'gaTriggerEvent',
                        'gaEventCategory': 'Form',
                        'gaEventAction': 'Enquiry',
                        'gaEventLabel': 'Success'
                      });

                    } else {

                      ////////////////////////////////////////////////////////////////////////////////////
                      // Error Block
                      ////////////////////////////////////////////////////////////////////////////////////
                      $('.js-contact-loader').css('display', 'none');
                      $('#js-form-contact').css('display', 'block');
                      $('.js-contact-error').css('display', 'block');

                    }


                  });


                }
              });

            } // END ELSE VALID RECAPTCHA
          }); // END GENERATE GOOGLE reCAptcha Token
        } // if #js-form-contact exists

        ////////////////////////////////////////////////////////////////////////////////////
        // Chosen Init
        ////////////////////////////////////////////////////////////////////////////////////
        $('body').find('select').each(function() {
          $(this).chosen();
        });
      }
    },

    ////////////////////////////////////////////////////////////////////////////////////
    // Home page
    ////////////////////////////////////////////////////////////////////////////////////
    home: {
      init: function () {}
    },
  };

  ////////////////////////////////////////////////////////////////////////////////////
  // The routing fires all common scripts, followed by the page specific scripts.
  // Add additional events for more control over timing e.g. a finalize event
  ////////////////////////////////////////////////////////////////////////////////////
  var UTIL = {
    fire: function (func, funcname, args) {
      var fire;
      var namespace = Sage;
      funcname = (funcname === undefined) ? 'init' : funcname;
      fire = func !== '';
      fire = fire && namespace[func];
      fire = fire && typeof namespace[func][funcname] === 'function';

      if (fire) {
        namespace[func][funcname](args);
      }
    },
    loadEvents: function () {
      // Fire common init JS
      UTIL.fire('common');

      // Fire page-specific init JS, and then finalize JS
      $.each(document.body.className.replace(/-/g, '_').split(/\s+/), function (i, classnm) {
        UTIL.fire(classnm);
        UTIL.fire(classnm, 'finalize');
      });

      // Fire common finalize JS
      UTIL.fire('common', 'finalize');
    }
  };

  // Register the router after every module has exposed its initializer below.
  loadAvivaSageEvents = UTIL.loadEvents;



})(jQuery); // Fully reference jQuery after this point.



////////////////////////////////////////////////////////////////////////
// SPECS BLOCK
////////////////////////////////////////////////////////////////////////
(() => {
  'use strict';

  const initializedSections = new WeakSet();

  const getPercentage = (styles, property, fallback) => {
    const value = parseFloat(styles.getPropertyValue(property));

    return Number.isFinite(value) ? value : fallback;
  };

  const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;

  const getClockwiseDelta = (fromAngle, toAngle) => (
    normalizeAngle(toAngle - normalizeAngle(fromAngle))
  );

  const positionHotspots = (media) => {
    const styles = window.getComputedStyle(media);
    const mediaBounds = media.getBoundingClientRect();
    const centerX = getPercentage(styles, '--specs-block-orbit-center-x', 50);
    const centerY = getPercentage(styles, '--specs-block-orbit-center-y', 50);
    const orbitSize = getPercentage(styles, '--specs-block-orbit-size', 82);
    const orbitDiameter = mediaBounds.width * (orbitSize / 100);
    const radiusX = orbitSize / 2;
    const radiusY = mediaBounds.height
      ? ((orbitDiameter / 2) / mediaBounds.height) * 100
      : radiusX;
    const labelOffset = getPercentage(styles, '--specs-block-label-offset', 12);
    const labelDistance = mediaBounds.width * (labelOffset / 100);

    media.querySelectorAll('.js-specs-block-hotspot').forEach((hotspot) => {
      const inputX = parseFloat(hotspot.dataset.inputX);
      const inputY = parseFloat(hotspot.dataset.inputY);

      if (!Number.isFinite(inputX) || !Number.isFinite(inputY)) {
        return;
      }

      let dx = inputX - centerX;
      let dy = inputY - centerY;

      if (0 === dx && 0 === dy) {
        dx = radiusX;
        dy = 0;
      }

      const scale = 1 / Math.sqrt(
        ((dx * dx) / (radiusX * radiusX)) +
        ((dy * dy) / (radiusY * radiusY))
      );
      const snappedX = centerX + (dx * scale);
      const snappedY = centerY + (dy * scale);
      const pixelDx = ((snappedX - centerX) / 100) * mediaBounds.width;
      const pixelDy = ((snappedY - centerY) / 100) * mediaBounds.height;
      const hotspotAngle = normalizeAngle(Math.atan2(pixelDx, -pixelDy) * (180 / Math.PI));
      const labelX = 0;
      const labelY = -labelDistance;

      hotspot.style.setProperty('--hotspot-x', `${snappedX}%`);
      hotspot.style.setProperty('--hotspot-y', `${snappedY}%`);
      hotspot.style.setProperty('--hotspot-label-x', `${labelX}px`);
      hotspot.style.setProperty('--hotspot-label-y', `${labelY}px`);
      hotspot.dataset.orbitAngle = hotspotAngle;
    });
  };

  const synchronizeHotspotFocus = (section, orbit) => {
    const hotspots = Array.from(section.querySelectorAll('.js-specs-block-hotspot'));
    const motionToggle = section.querySelector('.js-specs-block-motion-toggle');
    const autoSwitchingEnabled = 'false' !== section.dataset.autoSwitching;
    const rotationDuration = AvivaUi.getDataNumber(section, 'rotationDuration', 12000);
    const rotationTransitionDuration = AvivaUi.getDataNumber(
      section,
      'rotationTransitionDuration',
      700
    );

    if (!hotspots.length || !orbit) {
      return null;
    }

    let animationFrame = null;
    let focusedIndex = null;
    let focusWindows = [];
    let rotationAnimation = null;
    let isOnScreen = true;
    let motionController = null;

    const setFocusedHotspot = (nextIndex) => {
      if (focusedIndex === nextIndex) {
        return;
      }

      hotspots.forEach((hotspot, index) => {
        const isActive = index === nextIndex;
        const card = hotspot.querySelector('.js-specs-block-hotspot-card');

        hotspot.classList.toggle('is-active', isActive);

        if (card) {
          card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        }
      });

      focusedIndex = nextIndex;
    };

    const getRotationProgress = () => {
      if (rotationAnimation && rotationAnimation.effect) {
        const progress = rotationAnimation.effect.getComputedTiming().progress;

        if (Number.isFinite(progress)) {
          return progress;
        }
      }

      return 0;
    };

    // With auto-switching off (or before the first build) the orbit spins on its CSS
    // animation, which the is-motion-paused class pauses.
    const isRotating = () => (
      rotationAnimation
        ? 'running' === rotationAnimation.playState
        : !orbit.classList.contains('is-js-controlled') && !section.classList.contains('is-motion-paused')
    );

    const updateFocus = () => {
      animationFrame = null;

      if (!section.isConnected || !autoSwitchingEnabled) {
        return;
      }

      if (!('animate' in orbit)) {
        setFocusedHotspot(0);
        return;
      }

      const progress = getRotationProgress();
      const nextIndex = focusWindows.findIndex((focusWindow, index) => (
        progress >= focusWindow.start &&
        (progress < focusWindow.end || (focusWindows.length - 1 === index && progress <= focusWindow.end))
      ));

      setFocusedHotspot(nextIndex < hotspots.length ? nextIndex : -1);

      if (isRotating()) {
        animationFrame = window.requestAnimationFrame(updateFocus);
      }
    };

    const pauseRotation = () => {
      if (rotationAnimation) {
        rotationAnimation.pause();
      }

      if (null !== animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }

      section.classList.add('is-motion-paused');
      section.classList.remove('is-motion-playing');
    };

    const resumeRotation = () => {
      if (!isOnScreen) {
        return;
      }

      section.classList.remove('is-motion-paused');
      section.classList.add('is-motion-playing');

      if (!rotationAnimation) {
        return;
      }

      rotationAnimation.play();

      if (null === animationFrame) {
        animationFrame = window.requestAnimationFrame(updateFocus);
      }
    };

    const buildRotationAnimation = () => {
      if (!autoSwitchingEnabled) {
        if (rotationAnimation) {
          rotationAnimation.cancel();
          rotationAnimation = null;
        }

        orbit.classList.remove('is-js-controlled');
        setFocusedHotspot(0);
        return;
      }

      const orbitStyles = window.getComputedStyle(orbit);
      const highlightAngle = getPercentage(
        orbitStyles,
        '--specs-block-orbit-highlight-angle',
        88
      );
      const targetAngles = hotspots.map((hotspot) => (
        normalizeAngle(parseFloat(hotspot.dataset.orbitAngle) - highlightAngle)
      ));

      if (targetAngles.some((angle) => !Number.isFinite(angle))) {
        return;
      }

      // Keep the current position when the animation is rebuilt (for example on resize).
      const previousTime = rotationAnimation ? rotationAnimation.currentTime : null;

      if (rotationAnimation) {
        rotationAnimation.cancel();
        rotationAnimation = null;
      }

      const rotations = [targetAngles[0]];

      for (let index = 1; index < targetAngles.length; index++) {
        rotations[index] = rotations[index - 1] + getClockwiseDelta(
          rotations[index - 1],
          targetAngles[index]
        );
      }

      let finalDelta = getClockwiseDelta(
        rotations[rotations.length - 1],
        targetAngles[0]
      );

      if (1 === rotations.length || finalDelta < 0.001) {
        finalDelta = 360;
      }

      const finalRotation = rotations[rotations.length - 1] + finalDelta;
      const segmentDuration = rotationDuration / rotations.length;
      const transitionDuration = Math.min(
        rotationTransitionDuration,
        segmentDuration / 2
      );
      const holdDuration = segmentDuration - transitionDuration;
      const keyframes = [];

      focusWindows = rotations.map((rotation, index) => {
        const segmentStart = index / rotations.length;
        const holdEnd = segmentStart + (holdDuration / rotationDuration);

        keyframes.push({
          offset: segmentStart,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        });
        keyframes.push({
          easing: 'ease-out',
          offset: holdEnd,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        });

        return {
          start: segmentStart,
          end: holdEnd,
        };
      });

      keyframes.push({
        offset: 1,
        transform: `translate(-50%, -50%) rotate(${finalRotation}deg)`,
      });

      orbit.classList.add('is-js-controlled');

      if (!('animate' in orbit)) {
        orbit.style.transform = `translate(-50%, -50%) rotate(${rotations[0]}deg)`;
        setFocusedHotspot(0);
        return;
      }

      orbit.style.removeProperty('transform');
      rotationAnimation = orbit.animate(keyframes, {
        duration: rotationDuration,
        fill: 'both',
        iterations: Infinity,
      });
      rotationAnimation.pause();
      focusedIndex = null;

      if (null !== previousTime) {
        rotationAnimation.currentTime = previousTime;
      }

      updateFocus();
    };

    buildRotationAnimation();

    // Only a browser without Web Animations leaves the orbit still, so only then is there
    // nothing to pause.
    const rotationToggle = !autoSwitchingEnabled || 'animate' in orbit ? motionToggle : null;

    motionController = AvivaUi.createMotionControl({
      toggle: rotationToggle,
      visibleLabel: rotationToggle
        ? rotationToggle.querySelector('.js-specs-block-motion-toggle-label')
        : null,
      play: resumeRotation,
      pause: pauseRotation,
      isPaused: () => !isRotating(),
      shouldAutoplay: () => isOnScreen,
    });

    // Pause only while off screen. Hovering or focusing must not alter the interval.
    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver((entries) => {
        isOnScreen = entries[entries.length - 1].isIntersecting;

        motionController.update();
      });
      visibilityObserver.observe(section);
    }

    return () => {
      buildRotationAnimation();
      motionController.update();
    };
  };

  const initializeSection = (section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const media = section.querySelector('.js-specs-block-media');
    const orbit = section.querySelector('.js-specs-block-orbit');

    if (!media) {
      return;
    }

    initializedSections.add(section);
    positionHotspots(media);
    const refreshRotation = synchronizeHotspotFocus(section, orbit);

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        positionHotspots(media);

        if (refreshRotation) {
          refreshRotation();
        }
      });
      resizeObserver.observe(media);
    } else {
      window.addEventListener('resize', () => {
        positionHotspots(media);

        if (refreshRotation) {
          refreshRotation();
        }
      }, { passive: true });
    }
  };

  const initializeSpecsBlocks = (context = document) => {
    context.querySelectorAll('.js-specs-block').forEach(initializeSection);
  };

  const initialize = () => {
    initializeSpecsBlocks();
  };

  AvivaModules.initSpecsBlocks = initialize;
})();



///////////////////////////////////////////////////////////////////////////////////////////
// Block: Map Module
///////////////////////////////////////////////////////////////////////////////////////////
(() => {
  'use strict';

  const mapModuleInitializedSections = new WeakSet();

  const parseMapModuleHotspotPosition = (value) => {
    if (!value) {
      return null;
    }

    const coordinates = value.match(/-?\d+(?:\.\d+)?/g);

    if (!coordinates || coordinates.length < 2) {
      return null;
    }

    return {
      x: Math.max(0, Math.min(100, parseFloat(coordinates[0]))),
      y: Math.max(0, Math.min(100, parseFloat(coordinates[1]))),
    };
  };

  const positionMapModuleOverlays = (section, mapModuleMobileQuery) => {
    section.querySelectorAll('.js-map-module-panel').forEach((panel) => {
      panel.querySelectorAll('.js-map-module-hotspot').forEach((hotspot) => {
        const mobilePosition = hotspot.dataset.hotspotMobilePosition;
        const imagePosition = parseMapModuleHotspotPosition(
          mapModuleMobileQuery.matches && mobilePosition
            ? mobilePosition
            : hotspot.dataset.hotspotPosition
        );

        if (!imagePosition) {
          hotspot.hidden = true;
          return;
        }

        hotspot.hidden = false;
        hotspot.style.setProperty('--map-hotspot-x', `${imagePosition.x}%`);
        hotspot.style.setProperty('--map-hotspot-y', `${imagePosition.y}%`);
      });

      panel.querySelectorAll('.js-map-module-label').forEach((label) => {
        const mobilePosition = label.dataset.labelMobilePosition;
        const imagePosition = parseMapModuleHotspotPosition(
          mapModuleMobileQuery.matches && mobilePosition
            ? mobilePosition
            : label.dataset.labelPosition
        );

        if (!imagePosition) {
          label.hidden = true;
          return;
        }

        label.hidden = false;
        label.style.setProperty('--map-label-x', `${imagePosition.x}%`);
        label.style.setProperty('--map-label-y', `${imagePosition.y}%`);
      });
    });
  };

  const initializeMapModuleSection = (section) => {
    if (mapModuleInitializedSections.has(section)) {
      return;
    }

    const carousel = section.querySelector('.js-map-module-carousel');
    const items = Array.from(section.querySelectorAll('.js-map-module-item'));
    const panels = Array.from(section.querySelectorAll('.js-map-module-panel'));
    const cards = items
      .map((item) => item.querySelector('.js-map-module-card'))
      .filter(Boolean);
    const mapModuleMobileQuery = AvivaUi.getBreakpointDownMedia('lg');

    if (!carousel || !items.length) {
      return;
    }

    mapModuleInitializedSections.add(section);
    positionMapModuleOverlays(section, mapModuleMobileQuery);

    let activeIndex = 0;

    const equalizeMobileCardHeights = () => {
      carousel.style.removeProperty('--map-module-mobile-card-height');

      if (!mapModuleMobileQuery.matches || !cards.length) {
        return;
      }

      const tallestCardHeight = cards.reduce((height, card) => (
        Math.max(height, card.scrollHeight)
      ), 0);

      if (tallestCardHeight > 0) {
        carousel.style.setProperty(
          '--map-module-mobile-card-height',
          `${Math.ceil(tallestCardHeight)}px`
        );
      }
    };

    const activateMap = (index) => {
      panels.forEach((panel) => {
        const isCurrentMapPanel = Number(panel.dataset.mapIndex) === index;

        panel.classList.toggle('is-active', isCurrentMapPanel);
        panel.setAttribute('aria-hidden', isCurrentMapPanel ? 'false' : 'true');
      });
    };

    const activateMobileSlide = (index) => {
      items.forEach((item) => {
        const isCurrentMapSlide = Number(item.dataset.mapIndex) === index;

        item.classList.toggle('is-active', isCurrentMapSlide);
      });

      activeIndex = index;
      activateMap(index);
    };

    const activateAccordion = (index) => {
      activeIndex = index;

      items.forEach((item) => {
        const itemIndex = Number(item.dataset.mapIndex);
        const isCurrentMapItem = itemIndex === index;
        const toggle = item.querySelector('.js-map-module-toggle');
        const mapCardElement = item.querySelector('.js-map-module-card');

        item.classList.toggle('is-active', isCurrentMapItem);

        if (toggle) {
          toggle.setAttribute('aria-expanded', isCurrentMapItem ? 'true' : 'false');
        }

        if (mapCardElement) {
          mapCardElement.setAttribute('aria-hidden', isCurrentMapItem ? 'false' : 'true');
        }
      });

      activateMap(index);
    };

    items.forEach((item) => {
      const toggle = item.querySelector('.js-map-module-toggle');

      if (!toggle) {
        return;
      }

      toggle.addEventListener('click', () => {
        if (!mapModuleMobileQuery.matches) {
          activateAccordion(Number(item.dataset.mapIndex));
        }
      });
    });

    const mobileCarousel = AvivaUi.initResponsiveFlickity({
      el: carousel,
      query: mapModuleMobileQuery,
      getOptions: () => ({
        adaptiveHeight: false,
        cellAlign: 'left',
        contain: false,
        draggable: items.length > 1,
        initialIndex: activeIndex,
        pageDots: items.length > 1,
        prevNextButtons: false,
        wrapAround: items.length > 1,
      }),
      onEnable: () => {
        items.forEach((item) => {
          const mapCardElement = item.querySelector('.js-map-module-card');

          if (mapCardElement) {
            mapCardElement.setAttribute('aria-hidden', 'false');
          }
        });

        activateMobileSlide(activeIndex);
      },
      onSelect: (selectedItemIndex) => {
        const selectedItem = items[selectedItemIndex];
        const selectedIndex = selectedItem ? Number(selectedItem.dataset.mapIndex) : 0;

        activateMobileSlide(selectedIndex);
      },
      onLayout: () => {
        positionMapModuleOverlays(section, mapModuleMobileQuery);
        equalizeMobileCardHeights();
      },
      onResetLayout: () => {
        carousel.style.removeProperty('--map-module-mobile-card-height');
      },
      onDisable: () => {
        activateAccordion(activeIndex);
      },
    });

    AvivaUi.watchLayoutSources({
      images: carousel.querySelectorAll('img'),
      persistentImages: section.querySelectorAll('.js-map-module-map-image img'),
      videos: carousel.querySelectorAll('video'),
      fonts: true,
      resize: true,
    }, mobileCarousel.relayout);
  };

  const initializeMapModules = (context = document) => {
    context.querySelectorAll('.js-map-module').forEach(initializeMapModuleSection);
  };

  const initializeMapModuleScripts = () => {
    initializeMapModules();
  };

  AvivaModules.initMapModules = initializeMapModuleScripts;
})();








//////////////////////////////////////////////////////////////////////////
// Block: Cards Block
//////////////////////////////////////////////////////////////////////////
( function () {
  'use strict';

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join( ',' );

  const initialiseCardsBlock = ( section ) => {
    if ( section.dataset.cardsInitialised === 'true' ) {
      return;
    }

    section.dataset.cardsInitialised = 'true';

    const carousel = section.querySelector( '[data-cards-carousel]' );

    if ( carousel ) {
      const cells = Array.from(
        carousel.querySelectorAll( '.js-cards-carousel-cell' )
      );
      const mobileOnly = carousel.hasAttribute( 'data-cards-carousel-mobile-only' );
      const mobileMedia = mobileOnly
        ? AvivaUi.getBreakpointDownMedia('md')
        : null;
      const cardsCarousel = AvivaUi.initResponsiveFlickity({
        el: carousel,
        query: mobileMedia,
        options: {
          cellAlign: 'left',
          contain: true,
          draggable: true,
          groupCells: false,
          pageDots: true,
          prevNextButtons: false,
          watchCSS: false,
          wrapAround: true,
        },
        onLayout: ({ flickity }) => {
          if ( ! flickity ) {
            return;
          }

          cells.forEach( ( cell ) => {
            cell.style.removeProperty( 'height' );
          } );

          const tallestCell = Math.max(
            0,
            ...cells.map( ( cell ) => cell.offsetHeight )
          );

          if ( tallestCell ) {
            cells.forEach( ( cell ) => {
              cell.style.height = `${ tallestCell }px`;
            } );
          }
        },
        onResetLayout: () => {
          cells.forEach( ( cell ) => {
            cell.style.removeProperty( 'height' );
          } );
        },
      });

      AvivaUi.watchLayoutSources({
        images: carousel.querySelectorAll( 'img' ),
        fonts: true,
        resize: true,
      }, cardsCarousel.relayout);
    }

    const modals = Array.from( section.querySelectorAll( '.js-cards-modal' ) );

    modals.forEach( ( modal ) => {
      document.body.appendChild( modal );
    } );

    let activeModal = null;
    let activeTrigger = null;
    let previousBodyOverflow = '';
    let modalCloseTimeout = null;
    let modalCloseCleanup = null;
    let closingModal = null;
    let modalTransitionId = 0;

    const cancelPendingModalClose = () => {
      if ( modalCloseTimeout ) {
        window.clearTimeout( modalCloseTimeout );
        modalCloseTimeout = null;
      }

      if ( modalCloseCleanup ) {
        modalCloseCleanup();
        modalCloseCleanup = null;
      }
    };

    const closeModal = () => {
      if ( ! activeModal ) {
        return;
      }

      const modal = activeModal;
      const trigger = activeTrigger;
      const transitionId = ++modalTransitionId;
      const dialog = modal.querySelector( '.js-cards-modal-dialog' );
      const transitionDuration = AvivaUi.getCssDuration(
        modal,
        '--cards-modal-dialog-duration'
      );

      modal.classList.remove( 'is-visible' );
      modal.classList.add( 'is-closing' );
      activeModal = null;
      activeTrigger = null;
      closingModal = modal;
      let closeFinished = false;

      const finishClose = () => {
        if ( closeFinished || transitionId !== modalTransitionId || activeModal === modal ) {
          return;
        }

        closeFinished = true;
        cancelPendingModalClose();
        modal.hidden = true;
        modal.classList.remove( 'is-closing' );
        document.body.style.overflow = previousBodyOverflow;
        document.documentElement.style.overflow = previousBodyOverflow;
        closingModal = null;

        if ( trigger ) {
          trigger.focus();
        }
      };

      if ( dialog && transitionDuration > 0 ) {
        const handleTransitionEnd = ( event ) => {
          if ( event.target === dialog && event.propertyName === 'transform' ) {
            finishClose();
          }
        };

        dialog.addEventListener( 'transitionend', handleTransitionEnd );
        modalCloseCleanup = () => {
          dialog.removeEventListener( 'transitionend', handleTransitionEnd );
        };
      }

      modalCloseTimeout = window.setTimeout(
        finishClose,
        transitionDuration > 0 ? transitionDuration + 100 : 0
      );
    };

    const openModal = ( modal, trigger ) => {
      if ( ! modal ) {
        return;
      }

      if ( activeModal === modal ) {
        return;
      }

      modalTransitionId += 1;
      activeModal = modal;
      activeTrigger = trigger;
      const isReopeningDuringClose = Boolean( closingModal );

      if ( ! isReopeningDuringClose ) {
        previousBodyOverflow = document.body.style.overflow;
      }

      cancelPendingModalClose();

      if ( closingModal && closingModal !== modal ) {
        closingModal.hidden = true;
        closingModal.classList.remove( 'is-closing' );
      }

      closingModal = null;

      modal.hidden = false;
      modal.classList.remove( 'is-closing' );
      modal.removeAttribute( 'aria-hidden' );
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      // Commit the hidden modal's starting styles before triggering its CSS transition.
      modal.getBoundingClientRect();
      modal.classList.add( 'is-visible' );

      const closeButton = modal.querySelector( '.js-cards-modal-close' );
      if ( closeButton ) {
        closeButton.focus();
      }
    };

    section.addEventListener( 'click', ( event ) => {
      const openButton = event.target.closest( '.js-cards-modal-open' );

      if ( openButton && section.contains( openButton ) ) {
        const modalId = openButton.dataset.cardsModalTarget;
        const modal = document.getElementById( modalId );

        openModal( modal && modals.includes( modal ) ? modal : null, openButton );
      }
    } );

    modals.forEach( ( modal ) => {
      modal.addEventListener( 'click', ( event ) => {
        if ( ! activeModal ) {
          return;
        }

        const closeButton = event.target.closest( '.js-cards-modal-close' );
        if ( closeButton || event.target === activeModal ) {
          closeModal();
        }
      } );
    } );

    const handleModalKeydown = ( event ) => {
      if ( ! activeModal ) {
        return;
      }

      if ( event.key === 'Escape' ) {
        event.preventDefault();
        closeModal();
        return;
      }

      if ( event.key !== 'Tab' ) {
        return;
      }

      const focusableElements = Array.from(
        activeModal.querySelectorAll( focusableSelector )
      ).filter( ( element ) => element.offsetParent !== null );

      if ( ! focusableElements.length ) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[ focusableElements.length - 1 ];

      if ( event.shiftKey && document.activeElement === firstElement ) {
        event.preventDefault();
        lastElement.focus();
      } else if ( ! event.shiftKey && document.activeElement === lastElement ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    modals.forEach( ( modal ) => {
      modal.addEventListener( 'keydown', handleModalKeydown );
    } );
  };

  const initialiseCardBlocks = () => {
    document.querySelectorAll( '[data-cards-block]' ).forEach( initialiseCardsBlock );
  };

  AvivaModules.initCardsBlocks = initialiseCardBlocks;
}() );




//////////////////////////////////////////////////////////////////
// Block: Carousel Block
//////////////////////////////////////////////////////////////////
(function () {
  'use strict';

  var carouselSelector = '.js-flexi-carousel';
  var carouselInstances = [];
  var viewportResizeFrame;
  var resizeTimeout;
  var resizeListenerAdded = false;
  var resizeDelay = 250;
  var layoutTransitionClass = 'is-layout-transitioning';
  var layoutTransitionOutClass = 'is-layout-transitioning-out';

  function getCellAlign(carousel) {
    var cellAlign = window.getComputedStyle(carousel).getPropertyValue('--carousel-cell-align').trim();

    return cellAlign || 'left';
  }

  function getTransitionDuration(carousel) {
    return AvivaUi.getCssDuration(carousel, '--carousel-slide-transition-duration');
  }

  // Flickity 2.3.0 compatibility adapter. This carousel relies on undocumented
  // layout state to preserve its wrap position. Re-test before upgrading Flickity.
  function createFlickityV230Adapter(flickity) {
    var requiredMethods = [
      'getSize',
      'positionCells',
      '_getWrapShiftCells',
      'setGallerySize',
      'positionSlider',
    ];
    var missingMethods = requiredMethods.filter(function (methodName) {
      return 'function' !== typeof flickity[methodName];
    });

    if (missingMethods.length) {
      return null;
    }

    return {
      canResize: function () {
        return flickity.isActive && !flickity.isDragging;
      },
      updateSize: function (cellAlign) {
        flickity.options.cellAlign = cellAlign;
        flickity.getSize();
      },
      normaliseWrapOffset: function (offset) {
        var wrapWidth = flickity.slideableWidth;

        if (!flickity.options.wrapAround || !wrapWidth) {
          return offset;
        }

        return ((offset + (wrapWidth / 2)) % wrapWidth + wrapWidth) % wrapWidth - (wrapWidth / 2);
      },
      normaliseWrapPosition: function () {
        var wrapWidth = flickity.slideableWidth;

        if (flickity.options.wrapAround && wrapWidth) {
          flickity.x = ((flickity.x % wrapWidth) + wrapWidth) % wrapWidth;
        }
      },
      positionCells: function (stationarySlideIndex) {
        var stationarySlide = flickity.slides[stationarySlideIndex];
        var stationaryTarget = stationarySlide && stationarySlide.target;
        var repositionedSlide;

        flickity.positionCells();
        flickity._getWrapShiftCells();
        flickity.setGallerySize();

        if (undefined !== stationaryTarget) {
          repositionedSlide = flickity.slides[stationarySlideIndex];
          flickity.x += stationaryTarget - repositionedSlide.target;
        }

        flickity.positionSlider();
      },
    };
  }

  function captureSlideRects(instance) {
    return instance.slides.map(function (slide) {
      var media = slide.querySelector('.js-flexi-carousel-media');
      var rect = media && media.getBoundingClientRect();

      if (!rect) {
        return null;
      }

      return {
        height: rect.height,
        left: rect.left,
        top: rect.top,
        width: rect.width,
      };
    });
  }

  function storeSlideRects(instance) {
    if (!instance.isLayoutTransitioning && !instance.isLayoutTransitionPending) {
      instance.slideRects = captureSlideRects(instance);
    }
  }

  function clearSlideTransitions(instance) {
    window.clearTimeout(instance.visualTransitionTimeout);
    window.cancelAnimationFrame(instance.visualTransitionFrame);
    window.cancelAnimationFrame(instance.visualTransitionSetupFrame);
    instance.isLayoutTransitionPending = false;
    instance.isLayoutTransitioning = false;

    instance.slides.forEach(function (slide) {
      var media = slide.querySelector('.js-flexi-carousel-media');

      slide.classList.remove(layoutTransitionClass);
      slide.classList.remove(layoutTransitionOutClass);

      if (media) {
        media.style.flex = '';
        media.style.transition = '';
        media.style.transform = '';
        media.style.width = '';
      }
    });
  }

  function animateSlideLayout(instance, previousRects, previousIndex, animatePosition) {
    var finalRects = captureSlideRects(instance);
    var transitionDuration = getTransitionDuration(instance.carousel);

    instance.isLayoutTransitionPending = false;

    if (!transitionDuration || !previousRects || previousRects.length !== finalRects.length) {
      instance.slideRects = finalRects;
      return;
    }

    instance.slides.forEach(function (slide, index) {
      var media = slide.querySelector('.js-flexi-carousel-media');
      var previousRect = previousRects[index];
      var finalRect = finalRects[index];

      if (!media || !previousRect || !finalRect) {
        return;
      }

      slide.classList.add(layoutTransitionClass);

      if (index === previousIndex) {
        slide.classList.add(layoutTransitionOutClass);
      }

      media.style.flex = '0 0 auto';
      media.style.transition = 'none';
      media.style.width = previousRect.width + 'px';
      media.style.transform = 'none';
    });

    instance.carousel.offsetHeight;

    instance.slides.forEach(function (slide, index) {
      var media = slide.querySelector('.js-flexi-carousel-media');
      var previousRect = previousRects[index];
      var currentRect;
      var offsetX;
      var offsetY;

      if (!media || !previousRect) {
        return;
      }

      currentRect = media.getBoundingClientRect();
      offsetX = animatePosition
        ? instance.flickityAdapter.normaliseWrapOffset(previousRect.left - currentRect.left)
        : 0;
      offsetY = animatePosition ? previousRect.top - currentRect.top : 0;
      media.style.transform = 'translate3d(' + offsetX + 'px, ' + offsetY + 'px, 0)';
    });

    instance.carousel.offsetHeight;
    instance.isLayoutTransitioning = true;

    instance.visualTransitionFrame = window.requestAnimationFrame(function () {
      instance.slides.forEach(function (slide, index) {
        var media = slide.querySelector('.js-flexi-carousel-media');
        var finalRect = finalRects[index];

        if (!media || !finalRect) {
          return;
        }

        media.style.transition = '';
        media.style.transform = 'translate3d(0, 0, 0)';
        media.style.width = finalRect.width + 'px';
      });
    });

    instance.visualTransitionTimeout = window.setTimeout(function () {
      clearSlideTransitions(instance);
      instance.slideRects = captureSlideRects(instance);
    }, transitionDuration + 50);
  }

  function resizeCarousel(instance) {
    var flickity = instance.flickity;
    var selectedElement = flickity.selectedElement;

    if (!instance.flickityAdapter.canResize()) {
      return;
    }

    clearSlideTransitions(instance);
    instance.flickityAdapter.updateSize(getCellAlign(instance.carousel));

    instance.flickityAdapter.normaliseWrapPosition();
    instance.flickityAdapter.positionCells();
    flickity.emitEvent('resize');
    flickity.selectCell(selectedElement, false, true);
    storeSlideRects(instance);
  }

  function resizeCarousels() {
    carouselInstances.forEach(function (instance) {
      resizeCarousel(instance);
    });
  }

  function scheduleCarouselResize() {
    window.clearTimeout(resizeTimeout);

    resizeTimeout = window.setTimeout(function () {
      window.cancelAnimationFrame(viewportResizeFrame);
      viewportResizeFrame = window.requestAnimationFrame(resizeCarousels);
    }, resizeDelay);
  }

  function initialiseCarousel(carousel) {
    if (carousel.dataset.carouselInitialised === 'true') {
      return;
    }

    if (typeof window.Flickity !== 'function') {
      return;
    }

    var slides = carousel.querySelectorAll('.js-flexi-carousel-slide');

    if (!slides.length) {
      return;
    }

    var flickity = new window.Flickity(carousel, {
      accessibility: true,
      adaptiveHeight: false,
      cellAlign: getCellAlign(carousel),
      cellSelector: '.js-flexi-carousel-slide',
      draggable: slides.length > 1,
      imagesLoaded: true,
      pageDots: slides.length > 1,
      prevNextButtons: false,
      resize: false,
      wrapAround: slides.length > 1,
    });
    var flickityAdapter = createFlickityV230Adapter(flickity);

    // An unsupported Flickity version keeps its standard behaviour, without the custom slide layout.
    if (!flickityAdapter) {
      window.addEventListener('resize', function () {
        flickity.resize();
      }, { passive: true });
      carousel.dataset.carouselInitialised = 'true';
      return;
    }

    var selectedIndex = flickity.selectedIndex;
    var instance = {
      carousel: carousel,
      flickity: flickity,
      flickityAdapter: flickityAdapter,
      isLayoutTransitionPending: false,
      isLayoutTransitioning: false,
      slides: Array.prototype.slice.call(slides),
    };

    storeSlideRects(instance);
    window.requestAnimationFrame(function () {
      storeSlideRects(instance);
    });
    window.setTimeout(function () {
      storeSlideRects(instance);
    }, getTransitionDuration(carousel) + 50);

    flickity.on('select', function () {
      var previousIndex = selectedIndex;
      var previousRects;
      var transitionWasInterrupted;

      if (selectedIndex === flickity.selectedIndex) {
        return;
      }

      transitionWasInterrupted = instance.isLayoutTransitioning || instance.isLayoutTransitionPending;
      previousRects = transitionWasInterrupted ? captureSlideRects(instance) : instance.slideRects;
      clearSlideTransitions(instance);
      selectedIndex = flickity.selectedIndex;
      instance.flickityAdapter.positionCells(previousIndex);
      instance.isLayoutTransitionPending = true;
      instance.visualTransitionSetupFrame = window.requestAnimationFrame(function () {
        animateSlideLayout(instance, previousRects, previousIndex, !transitionWasInterrupted);
      });
    });

    flickity.on('dragMove', function () {
      storeSlideRects(instance);
    });

    flickity.on('pointerDown', function () {
      storeSlideRects(instance);
    });

    flickity.on('settle', function () {
      storeSlideRects(instance);
    });

    flickity.on('uiChange', function () {
      storeSlideRects(instance);
    });

    carouselInstances.push(instance);

    if (!resizeListenerAdded) {
      window.addEventListener('resize', scheduleCarouselResize);
      resizeListenerAdded = true;
    }

    carousel.dataset.carouselInitialised = 'true';
  }

  function initialiseCarousels() {
    document.querySelectorAll(carouselSelector).forEach(initialiseCarousel);
  }

  AvivaModules.initCarousels = initialiseCarousels;
}());










////////////////////////////////////////////////////////////////
// Block: Content Showcase
////////////////////////////////////////////////////////////////
(() => {
  'use strict';

  const initializeContentShowcases = () => {
    document.querySelectorAll('.js-content-showcase').forEach((showcase) => {
      if (showcase.dataset.contentShowcaseInitialized === 'true') {
        return;
      }

      const navigation = showcase.querySelector('.js-content-showcase-navigation');
      const navigationItems = Array.from(showcase.querySelectorAll('.js-content-showcase-navigation-item'));
      const images = showcase.querySelector('.js-content-showcase-images');
      const imagePanels = Array.from(showcase.querySelectorAll('.js-content-showcase-image-panel'));
      const copyPanels = Array.from(showcase.querySelectorAll('.js-content-showcase-copy-panel'));
      const previousButton = showcase.querySelector('.js-content-showcase-previous');
      const nextButton = showcase.querySelector('.js-content-showcase-next');

      if (!navigation || !images || !navigationItems.length) {
        return;
      }

      showcase.dataset.contentShowcaseInitialized = 'true';

      const mobileQuery = AvivaUi.getBreakpointDownMedia('md');
      let activeIndex = 0;
      let hideInactivePanelsTimer = null;
      let imageCarouselEnabled = false;
      let isSynchronizingCarousels = false;
      let navigationCarousel = null;
      let imageCarousel = null;
      let transitionToken = 0;

      const getPanelIndex = (panel) => Number(
        panel.hasAttribute('data-showcase-image-index')
          ? panel.dataset.showcaseImageIndex
          : panel.dataset.showcaseCopyIndex
      );

      const setActiveItem = (index) => {
        if (index < 0 || index >= navigationItems.length) {
          return;
        }

        activeIndex = index;
        transitionToken += 1;

        const currentTransitionToken = transitionToken;
        const transitionDuration = AvivaUi.getCssDuration(
          showcase,
          '--content-showcase-transition-duration'
        );
        const panels = [...imagePanels, ...copyPanels];

        navigationItems.forEach((item, itemIndex) => {
          const isActive = itemIndex === activeIndex;

          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        if (previousButton) {
          previousButton.disabled = activeIndex === 0;
        }

        if (nextButton) {
          nextButton.disabled = activeIndex === navigationItems.length - 1;
        }

        panels.forEach((panel) => {
          const panelIndex = getPanelIndex(panel);
          const isCarouselImagePanel = imageCarouselEnabled
            && panel.hasAttribute('data-showcase-image-index');

          if (isCarouselImagePanel || panelIndex === activeIndex) {
            panel.hidden = false;
          }
        });

        window.clearTimeout(hideInactivePanelsTimer);

        window.requestAnimationFrame(() => {
          if (currentTransitionToken !== transitionToken) {
            return;
          }

          panels.forEach((panel) => {
            const panelIndex = getPanelIndex(panel);
            const isActive = panelIndex === activeIndex;

            panel.classList.toggle('is-active', isActive);
            panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });

          hideInactivePanelsTimer = window.setTimeout(() => {
            if (currentTransitionToken !== transitionToken) {
              return;
            }

            panels.forEach((panel) => {
              const isCarouselImagePanel = imageCarouselEnabled
                && panel.hasAttribute('data-showcase-image-index');

              if (!isCarouselImagePanel && !panel.classList.contains('is-active')) {
                panel.hidden = true;
              }
            });
          }, transitionDuration);
        });
      };

      const getCarouselInstance = (carousel) => carousel
        ? carousel.getInstance()
        : null;

      const selectCarouselItem = (carousel, index) => {
        const flickity = getCarouselInstance(carousel);

        if (!flickity || flickity.selectedIndex === index) {
          return;
        }

        flickity.select(index);
      };

      const selectItem = (index, source = null) => {
        if (index < 0 || index >= navigationItems.length) {
          return;
        }

        if (index !== activeIndex) {
          setActiveItem(index);
        }

        if (isSynchronizingCarousels) {
          return;
        }

        isSynchronizingCarousels = true;

        try {
          if (source !== 'navigation') {
            selectCarouselItem(navigationCarousel, index);
          }

          if (source !== 'images') {
            selectCarouselItem(imageCarousel, index);
          }
        } finally {
          isSynchronizingCarousels = false;
        }
      };

      setActiveItem(activeIndex);

      navigationCarousel = AvivaUi.initResponsiveFlickity({
        el: navigation,
        query: mobileQuery,
        enabled: (mediaQuery) => mediaQuery.matches && navigationItems.length > 1,
        getOptions: () => ({
          accessibility: true,
          adaptiveHeight: false,
          cellAlign: 'center',
          contain: false,
          draggable: true,
          initialIndex: activeIndex,
          pageDots: false,
          prevNextButtons: false,
          wrapAround: false,
        }),
        onSelect: (selectedIndex) => {
          selectItem(selectedIndex, 'navigation');
        },
        onDisable: () => {
          setActiveItem(activeIndex);
        },
      });

      imageCarousel = AvivaUi.initResponsiveFlickity({
        el: images,
        query: mobileQuery,
        enabled: (mediaQuery) => {
          imageCarouselEnabled = mediaQuery.matches
            && imagePanels.length > 1
            && imagePanels.length === navigationItems.length;

          if (imageCarouselEnabled) {
            imagePanels.forEach((panel) => {
              panel.hidden = false;
            });
          }

          return imageCarouselEnabled;
        },
        getOptions: () => ({
          accessibility: true,
          adaptiveHeight: false,
          cellAlign: 'left',
          cellSelector: '.js-content-showcase-image-panel',
          contain: true,
          draggable: true,
          initialIndex: activeIndex,
          pageDots: false,
          prevNextButtons: false,
          wrapAround: false,
        }),
        onSelect: (selectedIndex) => {
          selectItem(selectedIndex, 'images');
        },
        onDisable: () => {
          imageCarouselEnabled = false;
          setActiveItem(activeIndex);
        },
      });

      if (previousButton) {
        previousButton.addEventListener('click', () => {
          selectItem(activeIndex - 1);
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          selectItem(activeIndex + 1);
        });
      }

      navigationItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          selectItem(index);
        });

        item.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return;
          }

          event.preventDefault();

          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = index + direction;

          if (nextIndex < 0 || nextIndex >= navigationItems.length) {
            return;
          }

          navigationItems[nextIndex].focus();
          navigationItems[nextIndex].click();
        });
      });

    });
  };

  AvivaModules.initContentShowcases = initializeContentShowcases;
})();







////////////////////////////////////////////////////////////////////////////
// BLOCK: PRICE ESTIMATE
////////////////////////////////////////////////////////////////////////////
(() => {
  'use strict';

  const initPriceEstimate = (section) => {
    if (section.dataset.priceEstimateInitialized === 'true') {
      return;
    }

    const ticker = section.querySelector('.js-price-estimate-ticker');

    if (!ticker) {
      return;
    }

    const previousButton = ticker.querySelector('.js-price-estimate-previous');
    const nextButton = ticker.querySelector('.js-price-estimate-next');
    const night = ticker.querySelector('.js-price-estimate-night');
    const price = ticker.querySelector('.js-price-estimate-price');
    const options = Array.from(ticker.querySelectorAll('.js-price-estimate-option'));

    if (!previousButton || !nextButton || !night || !price || !options.length) {
      return;
    }

    let currentIndex = 0;

    const updateTicker = () => {
      const currentOption = options[currentIndex];

      night.textContent = currentOption.dataset.night || '';
      price.textContent = currentOption.dataset.price || '';
      previousButton.disabled = currentIndex === 0;
      nextButton.disabled = currentIndex === options.length - 1;
    };

    previousButton.addEventListener('click', () => {
      if (currentIndex === 0) {
        return;
      }

      currentIndex -= 1;
      updateTicker();
    });

    nextButton.addEventListener('click', () => {
      if (currentIndex === options.length - 1) {
        return;
      }

      currentIndex += 1;
      updateTicker();
    });

    section.dataset.priceEstimateInitialized = 'true';
    updateTicker();
  };

  const initPriceEstimates = () => {
    document.querySelectorAll('.js-price-estimate').forEach(initPriceEstimate);
  };

  AvivaModules.initPriceEstimates = initPriceEstimates;
})();

////////////////////////////////////////////////////////////////
// Module videos: autoplay only when the visitor allows motion,
// with a visible play/pause control either way.
////////////////////////////////////////////////////////////////
(() => {
  const initializeVideoBlock = (videoBlock) => {
    const video = videoBlock.querySelector('.js-module-video');
    const toggle = videoBlock.querySelector('.js-video-block-motion-toggle');

    if (!video) {
      return;
    }

    const controller = AvivaUi.createMotionControl({
      toggle,
      visibleLabel: toggle ? toggle.querySelector('.js-video-block-motion-toggle-label') : null,
      play: () => video.play(),
      pause: () => video.pause(),
      isPaused: () => video.paused || video.ended,
    });

    video.addEventListener('play', controller.sync);
    video.addEventListener('pause', controller.sync);
    video.addEventListener('ended', controller.sync);
  };

  const initializeVideoBlocks = () => {
    document.querySelectorAll('.js-video-block').forEach(initializeVideoBlock);
  };

  AvivaModules.initModuleVideos = initializeVideoBlocks;
})();


////////////////////////////////////////////////////////////////
// Footer: Footer Video
// Loads the source near the viewport and delegates motion preferences and the
// accessible play/pause control to the shared motion controller.
////////////////////////////////////////////////////////////////
(() => {
  const initFooterVideos = () => {
    document.querySelectorAll('.js-footer-video-hero').forEach((hero) => {
      const video = hero.querySelector('.js-footer-video[data-src]');
      const toggle = hero.querySelector('.js-footer-video-toggle');
      let isNearViewport = false;
      let controller = null;

      if (!video) {
        return;
      }

      const loadFooterVideo = () => {
        if (!video.dataset.src) {
          return undefined;
        }

        if (!video.getAttribute('src')) {
          video.src = video.dataset.src;
          video.load();
        }

        return video.play();
      };

      const syncController = () => {
        if (controller) {
          controller.sync();
        }
      };

      video.addEventListener('play', syncController);
      video.addEventListener('pause', syncController);
      video.addEventListener('ended', syncController);

      controller = AvivaUi.createMotionControl({
        toggle,
        visibleLabel: toggle
          ? toggle.querySelector('.js-footer-video-toggle-label')
          : null,
        play: loadFooterVideo,
        pause: () => video.pause(),
        isPaused: () => video.paused || video.ended,
        shouldAutoplay: () => isNearViewport,
      });

      const startFooterVideo = () => {
        isNearViewport = true;
        controller.update();
      };

      if (!('IntersectionObserver' in window)) {
        if (document.readyState === 'complete') {
          startFooterVideo();
        } else {
          window.addEventListener('load', startFooterVideo, { once: true });
        }
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            startFooterVideo();
          }
        });
      }, { rootMargin: '0px 0px 600px 0px' });

      observer.observe(hero);
    });
  };

  AvivaModules.initFooterVideos = initFooterVideos;
})();

jQuery(document).ready(loadAvivaSageEvents);
